// ExoCalSidebar.js
import {
  Box,
  Button,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState, createElement } from 'react';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import CalendarSelect from './types/CalendarSelect';
import ExoAlert from '../ExoAlert';
import { CalMeetingForm } from './types/meeting/CalMeetingForm';
import { CalSickForm } from './types/sick/CalSickForm';
import { CalMeetingShow } from './types/meeting/CalMeetingShow';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import Cookies from 'universal-cookie';
import moment from 'moment';
import uuid from 'react-uuid';
import findDifferences from '../findDifferencesInDataCollection';
import { validateLink } from '../../special/validateLink';

export const ExoCalSidebar = ({
  open,
  onClose,
  data,
  calendars,
  onFeedback,
  onUpdate,
  onDelete,
  editMode,
  setEditMode,
  useSidebar,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const cookies = new Cookies();

  const userId = cookies.get('userId');
  const selectedCalId = cookies.get('selectedCalId');

  const [type, setType] = useState('meeting');
  const [dataCollection, setDataCollection] = useState({});
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const typeEditMap = {
    meeting: CalMeetingForm,
    sick: CalSickForm,
  };

  const typeShowMap = {
    meeting: CalMeetingShow,
  };

  useEffect(() => {
    if (data && data.startDateTime && data.endDateTime) {
      if (data.id) {
        handleLoadEvent(data.id);
      } else {
        setDataCollection(meetingDefaultData(data, dataCollection));
      }
    }
  }, [data]);

  function handleLoadEvent(eventId) {
    DBRequest({
      config,
      path: `calendar-events/${eventId}`,
      onResponse: handleResponse,
      onLoading: setIsLoading,
    });
  }

  function handleClose(e) {
    if (e) e.preventDefault();
    setDataCollection({});
    setEditMode(false);
    onClose();
  }

  function handleResponse(res) {
    const event = res.data;
    const { userCalendar, startDateTime, endDateTime } = event;

    const userCal = calendars.find((el) => el.id === userCalendar.id);
    const firstCalWrite = calendars.find((el) => el.writePermissions === true);

    setSelectedCalendar(userCal || firstCalWrite);

    setDataCollection({
      ...event,
      startTime: startDateTime,
      startDate: startDateTime,
      endTime: endDateTime,
      endDate: endDateTime,
    });
  }

  function meetingDefaultData(event) {
    const { startDateTime, endDateTime, wholeDay = true } = event;
    const now = moment();
    const roundedStart = now
      .clone()
      .add(30 - (now.minute() % 30), 'minutes')
      .startOf('minute');
    const roundedEnd = roundedStart.clone().add(30, 'minutes');

    const reminders = event.wholeDay
      ? [
          {
            remindAt: moment(startDateTime).subtract(1, 'days'),
            uuid: uuid(),
            type: 'daysBefore',
            value: 1,
          },
        ]
      : [];

    return {
      startTime: wholeDay ? roundedStart.toISOString() : startDateTime,
      startDate: startDateTime,
      endTime: wholeDay ? roundedEnd.toISOString() : endDateTime,
      endDate: endDateTime,
      wholeDay: wholeDay ? '1' : '0',
      meetingIsConfirmed: '0',
      label: t('New Meeting'),
      type: 'meeting',
      calendarId: selectedCalId,
      blockTime: true,
      id: event.id,
      owner: { id: userId },
      reminders: reminders,
    };
  }

  function handleCalendarChange(newCal, isInit) {
    if (
      !isInit &&
      dataCollection.id &&
      dataCollection.calendar?.id !== newCal.id
    )
      handleMoveMeeting(dataCollection, newCal);
    setSelectedCalendar(newCal);
  }

  function handleMoveMeeting(meeting, newCal) {
    DBRequest({
      config,
      path: `calendar-events/${meeting.id}/move`,
      method: API_PATCH({
        fromCalendar: meeting.userCalendar,
        toCalendar: newCal,
      }),
      onFeedback,
      onError: () => setSelectedCalendar(meeting.userCalendar),
      onResponse: (res) =>
        onUpdate({
          ...res.data,
          calendar: res.data.userCalendar,
          color: res.data.userCalendar.color,
          writePermissions: true,
        }),
    });
  }

  function handleSubmit(sendEmails) {
    const startMoment = moment(dataCollection.startTime);
    const endMoment = moment(dataCollection.endTime);

    const reqData = {
      ...dataCollection,
      startDateTime: startMoment.toISOString(),
      endDateTime: endMoment.toISOString(),
      sendEmails: Boolean(sendEmails),
      calendarId: selectedCalendar.id,
    };

    let dataToSend = findDifferences(data, reqData);
    if (reqData.meetingPartner) {
      dataToSend['meetingPartnerType'] =
        reqData.meetingPartnerType || data.meetingPartnerType;
    }

    dataToSend = {
      ...dataToSend,
      onlineMeeting: dataToSend.onlineMeeting
        ? {
            ...dataToSend.onlineMeeting,
            url: validateLink(dataToSend.onlineMeeting.url),
          }
        : null,
    };

    DBRequest({
      config,
      path: dataCollection.id
        ? `calendar-events/${dataCollection.id}`
        : 'calendar-events',
      method: dataCollection.id ? API_PATCH(dataToSend) : API_POST(reqData),
      onFeedback,
      onResponse: (res) => {
        setDataCollection({});
        setOpenAlert(false);
        onUpdate({
          ...res.data,
          calendar: selectedCalendar,
          color: selectedCalendar.color,
        });
        setEditMode(false);
      },
      onError: () => setOpenAlert(false),
    });
    onClose();
  }

  function handleDelete() {
    DBRequest({
      config,
      path: `calendar-events/${dataCollection.id}`,
      method: API_DELETE,
      onFeedback,
      onResponse: () => onDelete(dataCollection.id),
    });
    onClose();
    setOpenAlert(null);
    setEditMode(false);
  }

  function requestSubmit() {
    let askMail = false;
    if (type === 'meeting' && dataCollection.participants?.length > 0) {
      askMail = dataCollection.participants.some((p) => p.email);
    }

    if (askMail)
      return setOpenAlert({
        type: 'info',
        message: t('Should an e-mail be sent to the participants?'),
        submitLabel: t('Yes'),
        cancelLabel: t('No'),
        onSubmit: () => handleSubmit(true),
        onCancel: () => handleSubmit(false),
      });
    handleSubmit();
  }

  function requestDelete() {
    if (type === 'meeting' && dataCollection.participants?.length > 0)
      return setOpenAlert({
        type: 'warning',
        message: t(
          'Should the appointment really be deleted? All participants will be notified by e-mail.'
        ),
        submitLabel: t('Yes'),
        cancelLabel: t('No'),
        onSubmit: handleDelete,
        onCancel: () => setOpenAlert(null),
      });
    else handleDelete();
  }

  return (
    <>
      {useSidebar && (
        <Box
          sx={{
            bgcolor: colors.glass,
            backdropFilter: 'blur(10px)',
            width: '400px',
            height: '100%',
            borderRadius: 2,
            p: 2,
            overflowY: 'auto',
          }}
        >
          {!data ? (
            // Render this if no event is selected
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  maxWidth: 300,
                  width: '100%',
                  p: 2,
                  borderRadius: 2,
                  overflowY: 'auto',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t('No Event Selected')}
                </Typography>
              </Box>
            </Box>
          ) : !editMode && data?.id ? (
            // View mode for selected event
            typeShowMap[type] &&
            createElement(typeShowMap[type], {
              dataCollection,
              onClose: handleClose,
              onEdit: () => setEditMode(true),
              isLoading,
              wholeDayData: data?.wholeDay,
              writePermissions: data?.writePermissions,
            })
          ) : (
            // Edit/create mode
            <>
              {data?.id && data?.writePermissions && (
                <IconButton
                  onClick={requestDelete}
                  className="top-2 right-[50px] z-20 backdrop-blur-sm"
                  sx={{
                    position: 'absolute',
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
              <Typography variant="h6" fontWeight={600} mb={2}>
                {dataCollection.id ? t('Edit Event') : t('New Event')}
              </Typography>

              <Box className="flex flex-col gap-2">
                <ToggleButtonGroup
                  size="small"
                  value={type}
                  exclusive
                  onChange={(e) => setType(e.target.value)}
                  sx={{ display: 'none' }}
                >
                  <ToggleButton value="meeting">{t('Meeting')}</ToggleButton>
                  <ToggleButton color="warning" value="sick">
                    {t('Sick')}
                  </ToggleButton>
                </ToggleButtonGroup>

                {type === 'meeting' && (
                  <CalendarSelect
                    calendars={calendars}
                    selectedCal={selectedCalendar}
                    onSelect={handleCalendarChange}
                  />
                )}

                {typeEditMap[type] &&
                  createElement(typeEditMap[type], {
                    dataCollection,
                    onChange: (d) =>
                      setDataCollection({ ...dataCollection, ...d }),
                    onValidationChange: setIsValid,
                  })}
              </Box>

              <Box className="flex justify-between mt-4">
                <Button onClick={handleClose} variant="outlined" color="error">
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={requestSubmit}
                  variant="contained"
                  color="success"
                  disabled={!isValid}
                >
                  {t('Submit')}
                </Button>
              </Box>
            </>
          )}

          <ExoAlert
            open={Boolean(openAlert)}
            alert={openAlert || {}}
            onClose={openAlert?.onCancel}
            onSubmit={openAlert?.onSubmit}
          />
        </Box>
      )}
    </>
  );
};
